import { Suspense, lazy, useCallback, useEffect, useState } from "react";

import FlatIcon from "./flatIcons";
import { onIdle, supportsWebGL } from "../lib/capabilities";
import "./IconMount.css";

const Icon3DView = lazy(() => import("../three/Icon3DView"));

/* ===========================================================================
   An icon slot.

   Renders the flat SVG immediately — that is what ships in the markup and what
   clients without WebGL keep. Once the 3D chunk has loaded and drawn, the flat
   copy fades out beneath the solid model, which is scissored into this
   element's rectangle by the shared canvas.

   The blue halo is a DOM layer rather than post-processing: it costs nothing,
   survives the no-WebGL path, and keeps the surrounding surface light.

   Decorative throughout — every icon here sits beside a heading that already
   says the same thing, so none of it is announced.
   =========================================================================== */

export default function IconMount({ name, tone, size = "md", active = false }) {
  const [enable3D, setEnable3D] = useState(false);
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    if (!supportsWebGL()) return undefined;
    return onIdle(() => setEnable3D(true));
  }, []);

  /* Fired from the model's first rendered frame, not from mount — the flat
     icon has to stay put until there is genuinely something behind it. */
  const onReady = useCallback(() => setDrawn(true), []);

  return (
    <span
      className={`icon-mount icon-mount--${size} icon-mount--${tone}`}
      data-drawn={drawn}
      data-active={active}
      aria-hidden="true"
    >
      <span className="icon-mount__glow" />
      <span className="icon-mount__flat">
        <FlatIcon name={name} />
      </span>

      {enable3D ? (
        <Suspense fallback={null}>
          <Icon3DView name={name} active={active} onReady={onReady} />
        </Suspense>
      ) : null}
    </span>
  );
}
